import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Plus, Edit, Trash2, Globe, X, Zap } from "lucide-react";

export default function AdminGeographyTab({
  countries,
  cities,
  selectedCountry,
  setSelectedCountry,
  showCountryModal,
  setShowCountryModal,
  showCityModal,
  setShowCityModal,
  editingCountry,
  setEditingCountry,
  editingCity,
  setEditingCity,
  countryForm,
  setCountryForm,
  cityForm,
  setCityForm,
  newHighlight,
  setNewHighlight,
  newVariation,
  setNewVariation,
  handleSaveCountry,
  handleSaveCity,
  handleDeleteCountry,
  handleDeleteCity,
  openEditCountry,
  openEditCity,
  openNewCity,
  seedGeographyData
}) {
  return (
    <div className="space-y-6">
      {/* Countries Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand" />
            <h3 className="font-semibold text-gray-900">Countries</h3>
            <span className="text-sm text-gray-500">({countries.length})</span>
          </div>
          <div className="flex gap-2">
            {countries.length === 0 && (
              <button onClick={seedGeographyData} className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-1">
                <Zap className="w-4 h-4" /> Seed UK Data
              </button>
            )}
            <button onClick={() => { setEditingCountry(null); setCountryForm({ code: "", name: "", slug: "" }); setShowCountryModal(true); }} className="px-3 py-1.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Country
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {countries.map(country => (
            <div key={country.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700 font-bold">
                  {country.code}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{country.name}</div>
                  <div className="text-sm text-gray-500">/{country.slug} · {country.city_count || 0} cities</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setSelectedCountry(country); }} className={`px-3 py-1.5 text-sm rounded-lg ${selectedCountry?.id === country.id ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  View Cities
                </button>
                <button onClick={() => openEditCountry(country)} className="p-2 text-gray-400 hover:text-brand hover:bg-gray-100 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDeleteCountry(country.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {countries.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No countries yet. Click "Seed UK Data" to add default UK cities.</p>
            </div>
          )}
        </div>
      </div>

      {/* Cities Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-brand" />
            <h3 className="font-semibold text-gray-900">
              {selectedCountry ? `Cities in ${selectedCountry.name}` : 'All Cities'}
            </h3>
            <span className="text-sm text-gray-500">
              ({selectedCountry ? cities.filter(c => c.country_id === selectedCountry.id).length : cities.length})
            </span>
          </div>
          <div className="flex gap-2">
            {selectedCountry && (
              <button onClick={() => setSelectedCountry(null)} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Show All
              </button>
            )}
            <button onClick={() => openNewCity(selectedCountry?.id)} className="px-3 py-1.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add City
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-sm text-gray-500">
              <tr>
                <th className="px-6 py-3 font-medium">City</th>
                <th className="px-6 py-3 font-medium">Slug</th>
                <th className="px-6 py-3 font-medium">Country</th>
                <th className="px-6 py-3 font-medium">Variations</th>
                <th className="px-6 py-3 font-medium">Venues</th>
                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(selectedCountry ? cities.filter(c => c.country_id === selectedCountry.id) : cities).map(city => (
                <tr key={city.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{city.name}</div>
                    {city.description && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">{city.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">/city/{city.slug}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{city.country?.name || city.country?.code || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {city.variations?.slice(0, 3).map((v) => (
                        <span key={v} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {v}
                        </span>
                      ))}
                      {city.variations?.length > 3 && (
                        <span className="text-xs text-gray-400">+{city.variations.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{city.venue_count || 0}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditCity(city)} className="p-2 text-gray-400 hover:text-brand hover:bg-gray-100 rounded-lg">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteCity(city.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {cities.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
              No cities yet. Add a country first, then add cities.
            </div>
          )}
        </div>
      </div>

      {/* Country Modal */}
      <AnimatePresence>
        {showCountryModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-brand" />
                {editingCountry ? 'Edit Country' : 'Add Country'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country Code (ISO) *</label>
                  <input type="text" value={countryForm.code} onChange={(e) => setCountryForm({ ...countryForm, code: e.target.value.toUpperCase() })} placeholder="GB" maxLength={2} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country Name *</label>
                  <input type="text" value={countryForm.name} onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })} placeholder="United Kingdom" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                  <input type="text" value={countryForm.slug} onChange={(e) => setCountryForm({ ...countryForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="uk" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowCountryModal(false); setEditingCountry(null); }} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button onClick={handleSaveCountry} className="flex-1 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark">
                  {editingCountry ? 'Update' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* City Modal */}
      <AnimatePresence>
        {showCityModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-2xl p-6 w-full max-w-lg my-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-brand" />
                {editingCity ? 'Edit City' : 'Add City'}
              </h3>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City Name *</label>
                    <input type="text" value={cityForm.name} onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })} placeholder="London" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                    <input type="text" value={cityForm.slug} onChange={(e) => setCityForm({ ...cityForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="london" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                  <select value={cityForm.country_id} onChange={(e) => setCityForm({ ...cityForm, country_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200">
                    <option value="">Select country...</option>
                    {countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude *</label>
                    <input type="number" step="any" value={cityForm.lat} onChange={(e) => setCityForm({ ...cityForm, lat: e.target.value })} placeholder="51.5074" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude *</label>
                    <input type="number" step="any" value={cityForm.lng} onChange={(e) => setCityForm({ ...cityForm, lng: e.target.value })} placeholder="-0.1278" className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={cityForm.description} onChange={(e) => setCityForm({ ...cityForm, description: e.target.value })} placeholder="A brief description of the city for remote workers..." rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200" />
                </div>
                
                {/* Variations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name Variations</label>
                  <p className="text-xs text-gray-500 mb-2">Alternative names/spellings (e.g., "Greater London", "Central London")</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {cityForm.variations?.map((v, i) => (
                      <span key={`var-${v}`} className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg flex items-center gap-1">
                        {v}
                        <button onClick={() => setCityForm({ ...cityForm, variations: cityForm.variations.filter((_, idx) => idx !== i) })} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={newVariation} onChange={(e) => setNewVariation(e.target.value)} placeholder="Add variation..." className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm" onKeyPress={(e) => { if (e.key === 'Enter' && newVariation.trim()) { e.preventDefault(); setCityForm({ ...cityForm, variations: [...(cityForm.variations || []), newVariation.trim()] }); setNewVariation(''); } }} />
                    <button onClick={() => { if (newVariation.trim()) { setCityForm({ ...cityForm, variations: [...(cityForm.variations || []), newVariation.trim()] }); setNewVariation(''); } }} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Highlights</label>
                  <p className="text-xs text-gray-500 mb-2">Featured areas or attractions (e.g., "Shoreditch tech hubs")</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {cityForm.highlights?.map((h, i) => (
                      <span key={`high-${h}`} className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded-lg flex items-center gap-1">
                        {h}
                        <button onClick={() => setCityForm({ ...cityForm, highlights: cityForm.highlights.filter((_, idx) => idx !== i) })} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={newHighlight} onChange={(e) => setNewHighlight(e.target.value)} placeholder="Add highlight..." className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm" onKeyPress={(e) => { if (e.key === 'Enter' && newHighlight.trim()) { e.preventDefault(); setCityForm({ ...cityForm, highlights: [...(cityForm.highlights || []), newHighlight.trim()] }); setNewHighlight(''); } }} />
                    <button onClick={() => { if (newHighlight.trim()) { setCityForm({ ...cityForm, highlights: [...(cityForm.highlights || []), newHighlight.trim()] }); setNewHighlight(''); } }} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowCityModal(false); setEditingCity(null); }} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button onClick={handleSaveCity} disabled={!cityForm.name || !cityForm.slug || !cityForm.country_id || !cityForm.lat || !cityForm.lng} className="flex-1 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed">
                  {editingCity ? 'Update' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
